import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../../backend";
import { ReactComponent as Error } from "../../assets/error.svg";
import { CategoryT } from "../../types/model";
import { getHttpHandler } from "../../utils/httpHandler";
import { stateList } from "../../utils/data";
import "./Signup.css";

interface signupUserData {
  name: string;
  email: string;
  password: string;
  interests: string[];
  gender: string;
  state: string;
}

const Signup = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<signupUserData>({
    name: "",
    email: "",
    password: "",
    interests: [],
    gender: "",
    state: "",
  });
  const [errorMess, setErrorMess] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [categories, setCategories] = useState<CategoryT[]>([]);

  const checkForDisabilitiy = (): boolean => {
    if (
      !userData?.email ||
      !userData?.password ||
      !userData?.name ||
      userData.interests.length < 1 ||
      !userData.state ||
      !userData.gender
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    // e.preventDefault();
    setUserData({
      ...userData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTags = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setUserData({
        ...userData,
        interests: [...userData.interests, e.target.name],
      });
    } else {
      setUserData({
        ...userData,
        interests: userData.interests.filter((int) => int !== e.target.name),
      });
    }
  };

  useEffect(() => {
    setIsDisabled(checkForDisabilitiy());
  }, [userData]);

  const signup = async (
    e: React.FormEvent<HTMLFormElement>,
    userData: signupUserData,
    clear: () => void
  ) => {
    e.preventDefault();
    if (checkForDisabilitiy()) {
      return setErrorMess("All fields are required");
    }
    if (userData.password?.length < 6) {
      return setErrorMess("Password must be of atleast 6 characters");
    }

    try {
      const signupResp = await fetch(`${API}/signup`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });
      const response = await signupResp.json();
      if (response?.user?.id) {
        navigate("/login", { replace: true });
        clear();
        setErrorMess("");
      } else {
        setErrorMess(response?.message || "Something went wrong");
      }
    } catch (error) {
      setErrorMess("Something went wrong");
      console.error("Something went wrong", error);
    }
  };

  const preloadCategories = async () => {
    const allCategories = await getHttpHandler({ url: `${API}/categories` });
    setCategories(allCategories);
  };

  useEffect(() => {
    preloadCategories();
  }, []);

  const getCheckedStatus = useCallback(
    (categoryId: string) => {
      // console.log(userData.interests.includes(categoryId));
      // userData.interests.indexOf(category._id) > -1
      return userData.interests.includes(categoryId);
    },
    [userData.interests]
  );

  return (
    <div className="signup-action-form">
      <form
        className="signup-form"
        onSubmit={(e) =>
          signup(e, userData, () =>
            setUserData({
              name: "",
              email: "",
              password: "",
              interests: [],
              state: "",
              gender: "",
            })
          )
        }
      >
        {categories?.length > 0 && (
          <div className="content">
            <h2 className="header">Sign Up</h2>
            <input
              onChange={handleChange}
              name="name"
              type="text"
              placeholder="Name"
              id="name"
            />
            <input
              onChange={handleChange}
              name="email"
              type="email"
              placeholder="Email"
              id="email"
            />
            <input
              onChange={handleChange}
              name="password"
              type="password"
              placeholder="Password"
              id="password"
            />
            <h4 className="label-heading">Interests</h4>
            <div className="interests-div">
              {categories?.map((category, ind) => (
                <div className="interest-div" key={ind}>
                  <input
                    key={ind}
                    onChange={handleTags}
                    name={category._id}
                    id={category._id}
                    value={category._id}
                    type="checkbox"
                    checked={getCheckedStatus(category._id)}
                  />
                  <label
                    className="interest"
                    key={category._id}
                    htmlFor={category._id}
                  >
                    {category.name}
                  </label>
                </div>
              ))}
            </div>
            <h4 className="label-heading gender-heading">Gender</h4>
            <div className="genders-div">
              <div className="gender-div">
                <input
                  onChange={handleChange}
                  name="gender"
                  type="radio"
                  value="Male"
                  id="gender"
                  checked={userData.gender === "Male"}
                />
                <label className="gender" htmlFor="gender">
                  Male
                </label>
              </div>
              <div className="gender-div">
                <input
                  onChange={handleChange}
                  name="gender"
                  type="radio"
                  value="Female"
                  id="gender"
                  checked={userData.gender === "Female"}
                />
                <label className="gender" htmlFor="gender">
                  Female
                </label>
              </div>
            </div>
            <div className="address-div">
              <h4 className="label-sub-heading">State:</h4>
              <select
                name="state"
                id="state"
                value={userData.state}
                onChange={handleChange}
              >
                <option hidden value={""}>
                  Select State
                </option>
                {stateList.map((state, ind) => (
                  <option value={state} key={ind}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <h2 className={`errorMessage ${!errorMess && "hideMessage"}`}>
              <Error className="errorIcon" /> {errorMess}
            </h2>
            <button type="submit" className="submit-btn" disabled={isDisabled}>
              Sign up
            </button>
            <Link to="/login" className="subLink">
              Already a user? Login
            </Link>
          </div>
        )}
      </form>
    </div>
  );
};

export default Signup;
